import React from 'react'

import Checkbox from '@mui/material/Checkbox'
import { useTheme } from '@mui/material/styles'
import get from 'lodash.get'
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'

import DatePicker from 'components/Template/MuiDatePicker'
import TextField from 'components/Template/MuiTextField'
import Typography from 'components/Template/MuiTypography'
import type { FormValue } from 'pages/Creation/Subject/Subject'
import { DATE_ISO_FORMAT, formatDate } from 'utils/dates'

interface BirthdayFieldProps {
    setValue: UseFormSetValue<FormValue>
    watch: UseFormWatch<FormValue>
    register: UseFormRegister<FormValue>
    errors: FieldErrors<FormValue>
    index: number
}

export default function ChildFormFields({ setValue, watch, index, register, errors }: BirthdayFieldProps) {
    const theme = useTheme()
    const birthdayFieldName: `subjects.${number}.birthday` = `subjects.${index}.birthday`
    const firstNameFieldname: `subjects.${number}.first_name` = `subjects.${index}.first_name`
    const lastNameFieldname: `subjects.${number}.last_name` = `subjects.${index}.last_name`
    const isExpectedBabyFieldname: `subjects.${number}.is_expected_baby` = `subjects.${index}.is_expected_baby`

    // Using `required:true` for a checkbox validation would raise an error when the checkbox value === false.
    // Use `.validate` instead.
    register(isExpectedBabyFieldname, { validate: (value) => typeof value == 'boolean' })
    const isExpectedBaby = watch(isExpectedBabyFieldname)
    register(birthdayFieldName, { required: { value: !isExpectedBaby, message: 'Please fill the birthday' } })
    const { ref: firstNameRef, ...firstNameProps } = register(firstNameFieldname, {
        required: { value: !isExpectedBaby, message: 'Please enter a first name' },
        maxLength: { value: 40, message: 'Too many characters' }
    })
    const { ref: lastNameRef, ...lastNameProps } = register(lastNameFieldname, {
        required: { value: !isExpectedBaby, message: 'Please enter a last name' },
        maxLength: { value: 40, message: 'Too many characters' }
    })

    return (
        <>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: theme.spacing(1),
                    marginBottom: theme.spacing(2)
                }}
            >
                <TextField
                    {...firstNameProps}
                    inputRef={firstNameRef}
                    label={`First name${isExpectedBaby ? ' (optional)' : ''}`}
                    name={firstNameFieldname}
                    error={Boolean(get(errors, firstNameFieldname))}
                    helperText={get(errors, `${firstNameFieldname}.message`)}
                    style={{ width: '100%' }}
                />
                <TextField
                    {...lastNameProps}
                    inputRef={lastNameRef}
                    label={`Last name${isExpectedBaby ? ' (optional)' : ''}`}
                    name={lastNameFieldname}
                    error={Boolean(get(errors, lastNameFieldname))}
                    helperText={get(errors, `${lastNameFieldname}.message`)}
                    style={{ width: '100%' }}
                />
            </div>
            {!isExpectedBaby && (
                <DatePicker
                    inputFormat={DATE_ISO_FORMAT}
                    openTo='year'
                    onChange={(date) => setValue(birthdayFieldName, formatDate(date, true))}
                    value={watch(birthdayFieldName)}
                    disableFuture
                    renderInput={(props) => (
                        <TextField
                            {...props}
                            placeholder='MM / DD / YYYY'
                            label='Birthday'
                            error={Boolean(get(errors, birthdayFieldName))}
                            helperText={get(errors, `${birthdayFieldName}.message`)}
                            style={{ width: '100%' }}
                        />
                    )}
                />
            )}
            <div style={{ display: 'flex', alignItems: 'center', marginTop: theme.spacing(2) }}>
                <Checkbox
                    checked={isExpectedBaby}
                    color='secondary'
                    onChange={(event) => setValue(isExpectedBabyFieldname, event.target.checked)}
                    name='expecting'
                    style={{ position: 'relative', left: -theme.spacing(1) }}
                />
                <Typography variant='body2'>We're expecting!</Typography>
            </div>
        </>
    )
}
